import * as Joi from 'joi';
import { CustomHelpers } from 'joi';
import dayjs from 'dayjs';
import {getYear} from './DateUtils';
import {
  ValidCeqTestDatesModel
} from '../modules/examRegistration/components/nonNationalExam/ExamSchoolingDetailsForm';

/**
 * Retrieve JOI schema object for dropdown retrieved from Master Code API. This method assumes
 * that the value of the dropdown is a string.
 *
 * @param isOptional whether the dropdown is optional or not
 * @param fieldName the name of the dropdown to be displayed in error messages
 */
export const getMasterCodeDropdownSchema = (isOptional: boolean, fieldName: string) => {
  if (isOptional) {
    return Joi.any().optional();
  }

  return Joi.string()
    .required()
    .messages({
      'any.required': `Please select ${fieldName}`,
      'string.empty': `Please select ${fieldName}`,
      'string.base': `Please select ${fieldName}`,
    });
};

export const getDateOfCeqSchema = (ceqValidDatesFromApi: ValidCeqTestDatesModel | undefined) => {
  if (
      !ceqValidDatesFromApi?.firstAcceptedCeqTestDate &&
      !ceqValidDatesFromApi?.lastAcceptedCeqTestDate
  ) {
    // always fail
    return Joi.string().valid('!!').messages({
      'any.only': `Invalid CEQ Test Date. Please contact SEAB Admin`,
    });
  }
  return Joi.date()
  .required()
  .max(dayjs(ceqValidDatesFromApi?.lastAcceptedCeqTestDate).toDate())
  .min(dayjs(ceqValidDatesFromApi?.firstAcceptedCeqTestDate).toDate())
  .messages({
    'date.base': 'Date of CEQ taken is not allowed to be empty',
    'date.min': 'Your CEQ Test Date has expired',
    'date.max': 'Invalid Future CEQ Test Date',
  });
};

export const getMasterCodeObjectDropdownSchema = (isOptional: boolean, fieldName: string) => {
  if (isOptional) {
    return Joi.any().optional();
  }

  return Joi.object()
    .required()
    .messages({
      'any.required': `${fieldName} is not allowed to be empty`,
      'object.base': `${fieldName} is not allowed to be empty`,
    });
};

/**
 * Retrieve JOI schema object for email. Assumes that email is string.
 */
export const getEmailSchema = (fieldName: string) =>
  Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      'string.based': `${fieldName} is not allowed to be empty`,
      'string.empty': `${fieldName} is not allowed to be empty`,
      'any.required': `${fieldName} is not allowed to be empty`,
      'string.email': 'Email should be in valid format',
    });

/**
 * getEmailSchemaPair and reEnterEmailSchemaPair are to be used together as a pair
 */
export const getEmailSchemaPair = (fieldName: string) =>
  Joi.string()
    .email({ tlds: { allow: false } })
    .messages({
      'any.required': `${fieldName} is not allowed to be empty`,
      'string.based': `${fieldName} is not allowed to be empty`,
      'string.empty': `${fieldName} is not allowed to be empty`,
      'any.only': `Invalid Email/Email do not match`,
      'string.email': `Invalid Email/Email do not match`,
    });

export const reEnterEmailSchemaPair = (fieldName: string) =>
  Joi.string()
    .email({ tlds: { allow: false } })
    .equal(Joi.ref('email'))
    .messages({
      'string.based': `${fieldName} is not allowed to be empty`,
      'string.empty': `${fieldName} is not allowed to be empty`,
      'any.required': `${fieldName} is not allowed to be empty`,
      'any.only': `Invalid Email/Email do not match`,
      'string.email': `Invalid Email/Email do not match`,
    });

/**
 * Custom phone number validation logic.
 *
 * @param isOptional whether the input is optional or not
 * @param value value from Joi.custom callback function parameter
 * @param helpers helpers from Joi.custom callback function parameter
 */
const validatePhoneNumber = (
  isOptional: boolean,
  maxLength: number,
  minLength: number | null,
  value: string | null,
  helpers: any
) => {
  if (value === undefined || value === null || value === '') {
    return isOptional ? value : helpers.error('phoneNumber.pattern.isEmpty');
  }

  // check whether phone number only contains number
  const numberOnlyRegex = /^[0-9]*$/g;
  if (!value.match(numberOnlyRegex)) {
    return helpers.error('phoneNumber.pattern.isInvalidPhoneNum');
  }

  if (minLength && value.length < minLength) {
    return helpers.error('phoneNumber.pattern.isTooShort');
  }

  if (value.length > maxLength) {
    return helpers.error('phoneNumber.pattern.isTooLong');
  }

  // check whether input is negative or has more than 8 digits
  const phoneNumberInteger = parseInt(value, 10);
  if (phoneNumberInteger < 0) {
    return helpers.error('phoneNumber.pattern.isInvalidPhoneNum');
  }

  return value;
};

// If the first letter of the NRIC starts with T or G, add 4 to the total.
const foreignerMargin = (val: string) => {
  const firstChar = val.charAt(0);
  if (firstChar === 'T' || firstChar === 'G') {
    return 4;
  }
  return firstChar === 'M' ? 3 : 0;
};

const validateNRICFirstCharCasesFunction = (
  firstChar: string,
  validateNRICOnly = false,
  validateFinOnly = false
) => ({
  S: firstChar === 'S' && !validateFinOnly,
  T: firstChar === 'T' && !validateFinOnly,
  F: firstChar === 'F' && !validateNRICOnly,
  G: firstChar === 'G' && !validateNRICOnly,
  M: firstChar === 'M' && !validateNRICOnly,
});

// Logic search on: https://ayumilovemaple.wordpress.com/2008/09/24/validation-singapore-nric-number-verification/
export const validateNRIC = (
  value: string,
  helpers: any,
  validateNRICOnly = false,
  validateFinOnly = false
) => {
  const newValue = value.toLocaleUpperCase();
  if (!newValue.match(new RegExp('^[A-Z]'))) {
    return helpers.error('string.pattern.startCharError');
  }
  let errorCode = '';
  const weight: number[] = [2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  const checkNRICDigit = ['J', 'Z', 'I', 'H', 'G', 'F', 'E', 'D', 'C', 'B', 'A'];
  const checkFINDigit = ['X', 'W', 'U', 'T', 'R', 'Q', 'P', 'N', 'M', 'L', 'K'];
  const checkNewFinDigit = ['K', 'L', 'J', 'N', 'P', 'Q', 'R', 'T', 'U', 'W', 'X'];

  for (let i = 0; i < weight.length; i += 1) {
    sum += weight[i] * Number(newValue.charAt(i + 1));
  }
  sum += foreignerMargin(newValue);
  const index: number = sum % 11;
  const firstChar = newValue.charAt(0);
  const lastChar = newValue.charAt(newValue.length - 1);

  const validateNRICFirstCharObject = validateNRICFirstCharCasesFunction(
    firstChar,
    validateNRICOnly,
    validateFinOnly
  );

  switch (true) {
    case validateNRICFirstCharObject.S:
    case validateNRICFirstCharObject.T:
      if (!/^[T|S]\d{7}/.test(newValue)) {
        errorCode = 'string.pattern.nric';
        break;
      }
      if (checkNRICDigit[index] === newValue.charAt(newValue.length - 1)) {
        return validateNricDob(value, helpers);
      }
      errorCode = 'string.pattern.nric';
      break;
    case validateNRICFirstCharObject.F:
    case validateNRICFirstCharObject.G:
    case validateNRICFirstCharObject.M:
      if (!(/^[G|F]\d{7}/.test(newValue) || /^M\d{7}/.test(newValue))) {
        errorCode = 'string.pattern.fin';
        break;
      }
      if (FinFGChecking(firstChar, lastChar, checkFINDigit, index)) {
        return validateNricDob(value, helpers);
      }
      if (FinMChecking(firstChar, lastChar, checkNewFinDigit, index)) {
        return validateNricDob(value, helpers);
      }
      errorCode = 'string.pattern.fin';
      break;
    default:
      errorCode = 'string.pattern.startCharError';
      break;
  }
  return helpers.error(errorCode);
};

const FinFGChecking = (firstChar: string, lastChar: string, dic: string[], index: number) =>
  (firstChar === 'F' || firstChar === 'G') && dic[index] === lastChar;

const FinMChecking = (firstChar: string, lastChar: string, dic: string[], index: number) =>
  firstChar === 'M' && dic[11 - index - 1] === lastChar;

/**
 * Get error messages for possible validation errors for phone number.
 *
 * @param fieldName name of the input to be displayed in error messages
 */
const getPhoneNumberErrorMessages = (fieldName: string) => ({
  'string.empty': `${fieldName} is not allowed to be empty`,
  'phoneNumber.pattern.isEmpty': `${fieldName} is not allowed to be empty`,
  'phoneNumber.pattern.isInvalidPhoneNum': 'Please enter a valid number',
  'phoneNumber.pattern.isTooLong': `${fieldName} is too long`,
  'phoneNumber.pattern.isTooShort': `${fieldName} is too short`,
});

/**
 * Retrieve JOI schema object for phone number. Assumes that phone number is string.
 *
 * @param isOptional whether the field is optional or not
 * @param fieldName name of the input to be displayed in error messages
 */
export const getPhoneNumberSchema = (
  isOptional: boolean,
  fieldName: string,
  maxLength = 15,
  minLength: number | null = null
) => {
  if (isOptional) {
    return Joi.string()
      .allow('', null)
      .custom((...joiParams) => validatePhoneNumber(isOptional, maxLength, minLength, ...joiParams))
      .messages(getPhoneNumberErrorMessages(fieldName));
  }

  return Joi.string()
    .custom((...joiParams) => validatePhoneNumber(isOptional, maxLength, minLength, ...joiParams))
    .messages(getPhoneNumberErrorMessages(fieldName));
};

export const validateDate = (isOptional: boolean, value: string, helpers: any) => {
  let errorCode = '';

  if (value) {
    const valueDay = dayjs(value);

    if (valueDay.isValid()) {
      if (valueDay.isAfter(dayjs())) {
        errorCode = 'date.pattern.isAfter';
      }
    } else {
      errorCode = 'date.pattern.notValid';
    }
  } else if (!isOptional) {
    errorCode = 'date.pattern.isEmpty';
  }

  if (errorCode !== '') {
    return helpers.error(errorCode);
  }
  return value;
};

export const validateSingaporeCountry = Joi.object().custom(
  (value: { codeId: number; codeValue: string; codeValueDescription: string }, ...joiParams) => {
    const singaporeNumber = value.codeValueDescription === 'Singapore (+65)';

    if (!singaporeNumber) {
      throw new Error('not singapore number');
    }
    return value;
  }
);

export const validateSingaporeCountryWithTransform = Joi.string().custom(
  (value: string, ...joiParams) => {
    const singaporeNumber = value === '65';

    if (!singaporeNumber) {
      throw new Error('not singapore number');
    }
    return value;
  }
);

/**
 * Return "optional" joi object if the validation is disabled.
 *
 * @param joiObject joi object with validation
 * @param disableValidation indicator to enable/disable the validation.
 */
export const getJoiObject = (
  joiObject: Joi.AnySchema,
  disableValidation = false
): Joi.AnySchema => {
  if (disableValidation) {
    return Joi.any().optional().allow(null, '');
  }
  return joiObject;
};

/**
 * Joi schema for nric / fin / passport field.
 *
 * @param disableValidation indicator to enable/disable the validation.
 */
export const getNricFinJoiObject = (disableValidation = false): Joi.AnySchema =>
  getJoiObject(
    Joi.when('IdType', {
      is: 'P',
      then: Joi.string().alphanum().required().max(20).messages({
        'any.required': 'NRIC/FIN/Passport No. is not allowed to be empty.',
        'string.empty': `NRIC/FIN/Passport No. is not allowed to be empty.`,
        'string.alphanum': `Passport No. should be alphanumeric.`,
        'string.max': 'Passport No. is too long.',
      }),
      otherwise: Joi.string()
        .alphanum()
        .required()
        .max(9)
        .custom((value, helpers) => validateNRIC(value, helpers), 'Validate NRIC/FIN/Passport No')
        .messages({
          'any.required': 'NRIC/FIN/Passport No. is not allowed to be empty.',
          'string.empty': `NRIC/FIN/Passport No. is not allowed to be empty.`,
          'string.alphanum': `NRIC / FIN should be alphanumeric.`,
          'string.max': 'NRIC / FIN is too long.',
          'string.pattern.nric': 'Invalid NRIC',
          'string.pattern.fin': 'Invalid FIN',
          'string.pattern.startCharError': 'Invalid NRIC / FIN',
          'string.pattern.nricYear':
            'Invalid NRIC format/NRIC format does not match with Year of Birth',
        }),
    }),
    disableValidation
  );

export const validateDobBasedOnNric = (dob: string, helpers: any) => {
  const { IdType, NricFin } = helpers.state.ancestors[0];
  if (IdType === 'I' && NricFin && dob) {
    const { valid, error } = validateNricYearOfBirth(NricFin, dob);
    if (!valid) {
      return helpers.error(error.type);
    }
  }
  return dob;
};

const validateNricDob = (nricFin: string, helpers: any) => {
  const idType = helpers.state.ancestors[0].IdType;
  const dob = helpers.state.ancestors[0].dateOfBirth;
  if (idType === 'I' && nricFin && dob) {
    const { valid, error } = validateNricYearOfBirth(nricFin, dob);
    if (!valid) {
      return helpers.error(error.type);
    }
  }
  return nricFin;
};

export const validateNricYearOfBirth = (nric: string, dob: string) => {
  const nricUpperCase = nric.toUpperCase();

  if (!nricUpperCase.startsWith('S') && !nricUpperCase.startsWith('T')) {
    return { valid: true, error: {} };
  }

  const yearOfBirth = getYear(dob);

  if (yearOfBirth < 1968 && nricUpperCase.startsWith('S')) {
    return { valid: true, error: {} };
  }

  const yearInString = yearOfBirth.toString();
  const yearLast2Digits = yearInString.slice(2, 4);
  const yearInNric = nric.slice(1, 3);

  if (
    ((nricUpperCase.startsWith('S') && yearInString.startsWith('19')) ||
      (nricUpperCase.startsWith('T') && yearInString.startsWith('20'))) &&
    yearInNric === yearLast2Digits
  ) {
    return { valid: true, error: {} };
  }

  return {
    valid: false,
    error: {
      message: 'Invalid NRIC format/NRIC format does not match with Year of Birth',
      type: 'string.pattern.nricYear',
    },
  };
};

export const validateHouseBlockNo = (value: string, helpers: CustomHelpers) =>
  /^\d/.test(value) ? value : helpers.error('string.pattern.houseBlockNo');

export const validateFloor = (value: string, helpers: CustomHelpers) =>
  /^\d{1,2}$/.test(value) ? value : helpers.error('string.pattern.floor');

export const validateUnit = (value: string, helpers: CustomHelpers) =>
  /^(\d*|\d+[a-zA-Z]*|[a-zA-Z]+\d+)$/.test(value) ? value : helpers.error('string.pattern.unit');

export const validateStreetName = (value: string, helpers: CustomHelpers) =>
  /^[a-zA-Z]/.test(value) ? value : helpers.error('string.pattern.streetName');

export const validatePostalCode = (value: string, helpers: CustomHelpers) =>
  /^\d{6}$/.test(value) ? value : helpers.error('string.pattern.postalCode');
